import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { compareCzech, routePrefixes, routes, sortBySearchMatch } from "@ckgo2/shared";
import type { ContentPage } from "@ckgo2/shared/types";
import { api, type ApiResponse } from "@/api/client";
import { WEB_URL } from "@/lib/webUrl";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import { uiLocale } from "@/i18n";
import { ExternalLink, FileText, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";

const STATUS_COLORS: Record<string, string> = {
  published: "badge-primary",
  draft: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

/**
 * Výpis jednoduchých stránek (řez 12, P16/F12.7). Dva RŮZNÉ prázdné stavy: žádná stránka
 * neexistuje (vysvětlení, k čemu stránky jsou a kde se objeví) × hledání nic nenašlo.
 */
export default function ContentPagesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["content-pages"],
    queryFn: () => api.get<ApiResponse<ContentPage[]>>("/api/admin/content-pages"),
  });
  const { data: destData } = useAdminDestinationList();
  const destName = (id?: string | null) =>
    id ? (destData?.data.find((d) => d.id === id)?.name ?? "—") : "—";

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/content-pages/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["content-pages"] }),
  });

  const pages = useMemo(() => data?.data ?? [], [data]);
  const visible = useMemo(
    () =>
      sortBySearchMatch(pages, search, {
        name: (p) => p.title,
        other: (p) => [p.slug],
        // Bez hledání a při stejné shodě abecedně (`compareCzech`); API řadí SQL binárně.
        tieBreak: (a, b) => compareCzech(a.title, b.title),
      }),
    [pages, search],
  );

  const statusLabels: Record<string, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
  };
  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(uiLocale(), {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="card text-error-600 text-center">
        {t("content.contentPages.list.loadError")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.contentPages.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("content.contentPages.list.subtitle")}
          </p>
        </div>
        <Link to="/content-pages/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("content.contentPages.list.newPage")}
        </Link>
      </div>

      {pages.length === 0 ? (
        <div
          data-prazdny="zadne"
          className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-700"
        >
          <FileText className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p className="font-semibold text-gray-900 dark:text-white">
            {t("content.contentPages.list.emptyTitle")}
          </p>
          <div className="mx-auto mt-2 max-w-xl space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <p>{t("content.contentPages.list.emptyPurpose")}</p>
            <p>
              {t("content.contentPages.list.emptyWhere", { path: `${routePrefixes.stranka}…` })}
            </p>
            <p>{t("content.contentPages.list.emptyDraft")}</p>
          </div>
          <Link to="/content-pages/new" className="btn-primary mt-5 inline-flex">
            <Plus className="h-5 w-5" />
            {t("content.contentPages.list.newPage")}
          </Link>
        </div>
      ) : (
        <>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("content.contentPages.list.searchPlaceholder")}
              aria-label={t("content.contentPages.list.searchPlaceholder")}
              className="input pl-9"
            />
          </div>

          {visible.length === 0 ? (
            <div
              data-prazdny="hledani"
              className="rounded-lg border border-dashed border-gray-300 p-8 text-center dark:border-gray-700"
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t("content.contentPages.list.searchEmpty")}
              </p>
              <button type="button" className="btn-secondary mt-3" onClick={() => setSearch("")}>
                {t("content.contentPages.list.clearSearch")}
              </button>
            </div>
          ) : (
            <div className="card overflow-x-auto p-0">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3">{t("content.contentPages.list.colTitle")}</th>
                    <th className="px-4 py-3">{t("content.contentPages.list.colAddress")}</th>
                    <th className="px-4 py-3">{t("content.contentPages.list.colStatus")}</th>
                    <th className="px-4 py-3">{t("content.contentPages.list.colDestination")}</th>
                    <th className="px-4 py-3">{t("content.contentPages.list.colUpdated")}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {visible.map((page) => (
                    <tr key={page.id}>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                        <Link to={`/content-pages/${page.id}`} className="hover:underline">
                          {page.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {routes.stranka(page.slug)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[page.status] ?? ""}`}
                        >
                          {statusLabels[page.status] ?? page.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {destName(page.destinationId)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {formatDate(page.updatedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {page.status === "published" && (
                            <a
                              href={`${WEB_URL}${routes.stranka(page.slug)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={t("content.contentPages.list.viewOnWeb", {
                                title: page.title,
                              })}
                              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                          <Link
                            to={`/content-pages/${page.id}`}
                            aria-label={t("content.contentPages.list.edit", { title: page.title })}
                            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </Link>
                          <button
                            type="button"
                            aria-label={t("content.contentPages.list.delete", {
                              title: page.title,
                            })}
                            onClick={() => {
                              if (
                                confirm(
                                  t("content.contentPages.list.confirmDelete", {
                                    title: page.title,
                                  }),
                                )
                              )
                                deleteMutation.mutate(page.id);
                            }}
                            className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
